import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Table,
    TableBody,
    TableCell,
    TableRow,
    IconButton,
    Tooltip,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { Device } from '../types/api';
import { decryptWithSalt, encryptWithSalt } from '../utils/lockbox3';
import { DEVICE_DIALOG_PRIORITY_FIELDS, DEFAULT_EXPORT_DEVICE_IGNORE_FIELDS } from '../constants/deviceFields';
import { exportToCSV } from '../utils/csvExport';
import { ExportDialog } from './ExportDialog';

interface DeviceDialogProps {
    device: Device | null;
    open: boolean;
    onClose: () => void;
}

const styles = {
    valueContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        flexWrap: 'wrap' as const
    }
};

export const DeviceDialog: React.FC<DeviceDialogProps> = ({ device, open, onClose }) => {
    const [decodedPasswords, setDecodedPasswords] = useState<Record<string, string>>({});
    const [exportOpen, setExportOpen] = useState(false);
    const [selectedFields, setSelectedFields] = useState<string[]>([]);

    // Reset decoded passwords when dialog closes
    useEffect(() => {
        if (!open) {
            setDecodedPasswords({});
            setExportOpen(false);
        }
    }, [open]);

    const handleCopyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text);
    };

    const handleDecodePassword = (encryptedPassword: string, deviceId: string) => {
        try {
            const salt = encryptWithSalt(deviceId, 'Dw4ny3');
            const password = decryptWithSalt(encryptedPassword, salt);
            
            setDecodedPasswords(prev => ({
                ...prev,
                [encryptedPassword]: password
            }));
        } catch (error) {
            console.error('Failed to decode password:', error);
        }
    };

    if (!device) return null;

    // Convert device object to array of entries for display, prioritizing DEVICE_DIALOG_PRIORITY_FIELDS
    const allEntries = Object.entries(device).map(([key, value]) => ({
        label: key.replace(/([A-Z])/g, ' $1').trim(), // Add spaces before capital letters
        value: value === null ? 'N/A' : 
               typeof value === 'boolean' ? value.toString() :
               typeof value === 'number' ? value.toString() :
               value,
        key: key
    }));

    // Sort entries to show priority fields first
    const deviceEntries = allEntries.sort((a, b) => {
        const aIndex = DEVICE_DIALOG_PRIORITY_FIELDS.indexOf(a.key);
        const bIndex = DEVICE_DIALOG_PRIORITY_FIELDS.indexOf(b.key);
        
        if (aIndex !== -1 && bIndex !== -1) {
            return aIndex - bIndex;
        }
        if (aIndex !== -1 && bIndex === -1) {
            return -1;
        }
        if (aIndex === -1 && bIndex !== -1) {
            return 1;
        }
        return 0;
    });

    const availableFields = Object.keys(device);

    const handleExport = async () => {
        // Export a single-row CSV for this device
        exportToCSV([device], 'device.csv', { fields: selectedFields.length ? selectedFields : undefined });
        setExportOpen(false);
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle>Device Details - {device.NetworkComputerName}</DialogTitle>
            <DialogContent>
                <Table>
                    <TableBody>
                        {deviceEntries.map(({ label, value, key }, index) => (
                            <TableRow key={`${device.DeviceId}-${key}` || `device-${index}`}>
                                <TableCell component="th" scope="row" sx={{ fontWeight: 'bold', width: '40%' }}>
                                    {label}
                                </TableCell>
                                <TableCell sx={{ verticalAlign: 'top', paddingTop: 2 }}>
                                    <div style={styles.valueContainer}>
                                    {(key === 'LocalAdminPassword' || key === 'VncPassword') && value && value !== '' ? (
                                        <>
                                            {decodedPasswords[value] || '••••••••'}
                                            <Tooltip title={decodedPasswords[value] ? "Hide password" : "Show password"}>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => {
                                                        if (decodedPasswords[value]) {
                                                            setDecodedPasswords(prev => {
                                                                const newState = { ...prev } as Record<string, string>;
                                                                delete newState[value];
                                                                return newState;
                                                            });
                                                        } else {
                                                            handleDecodePassword(value, device.DeviceId || device.NetworkComputerName);
                                                        }
                                                    }}
                                                >
                                                    {decodedPasswords[value] ? (
                                                        <VisibilityOffIcon fontSize="small" />
                                                    ) : (
                                                        <VisibilityIcon fontSize="small" />
                                                    )}
                                                </IconButton>
                                            </Tooltip>
                                            {decodedPasswords[value] && (
                                                <Tooltip title="Copy to clipboard">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleCopyToClipboard(decodedPasswords[value])}
                                                    >
                                                        <ContentCopyIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </>
                                    ) : (
                                        <>
                                            {value}
                                            {typeof value === 'string' && value !== 'N/A' && (
                                                <Tooltip title="Copy to clipboard">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleCopyToClipboard(value)}
                                                    >
                                                        <ContentCopyIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </>
                                    )}
                                    </div>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => { setSelectedFields(DEVICE_DIALOG_PRIORITY_FIELDS); setExportOpen(true); }} variant="outlined" color="primary">
                    Export to CSV
                </Button>
                <Button onClick={onClose} variant="outlined" color="primary">
                    Close
                </Button>
            </DialogActions>
            <ExportDialog
                open={exportOpen}
                onClose={() => setExportOpen(false)}
                availableFields={availableFields}
                selectedFields={selectedFields}
                setSelectedFields={(updater) => setSelectedFields(prev => updater(prev))}
                onExport={handleExport}
                visibleDefaultFields={DEVICE_DIALOG_PRIORITY_FIELDS}
                ignoreFields={DEFAULT_EXPORT_DEVICE_IGNORE_FIELDS}
            />
        </Dialog>
    );
}; 